import { get, post } from '@/lib/api';
import type { Chamado } from '@/features/tickets/types';
import type {
  DefinicaoDeFluxo,
  Diagnostico,
  PassoAplicado,
  PedidoDeTransicao,
  RegistroDeTransicao,
  TransicoesDoChamado,
  VersaoDeFluxo,
} from '@/features/workflow/tipos';

/**
 * Acesso à API do motor de fluxo.
 *
 * Uma função por rota que existe em `workflow.controller.ts`, sem nenhuma
 * inventada — e o que falta está **anotado**, não simulado:
 *
 *   • não há rota que **leia** o grafo de uma versão. `listDefinitions`
 *     seleciona `id`, `versionNumber`, `status` e `publishedAt`; `saveDraft` e
 *     `publish` devolvem a linha inteira, com `graph`. O editor de rascunho
 *     lida com isso na cara do usuário, em vez de fingir que carregou.
 *
 *   • não há rota que **liste** aprovações. `GET /approvals/:id` exige o id, e
 *     nada o entrega — nem uma fila do usuário, nem as aprovações de um
 *     chamado. Por isso não existe tela de "minhas aprovações" nesta entrega:
 *     ela precisaria adivinhar identificadores.
 */

const ADMIN = '/admin/workflows';
const CHAMADOS = '/tickets';

// ── catálogo ───────────────────────────────────────────────────────────────

export const listarDefinicoes = () => get<DefinicaoDeFluxo[]>(ADMIN);

export interface NovaDefinicao {
  key: string;
  name: string;
  entityType: string;
  description?: string;
}

export const criarDefinicao = (corpo: NovaDefinicao) =>
  post<DefinicaoDeFluxo>(ADMIN, corpo);

/**
 * Guarda o grafo como rascunho.
 *
 * Sem validação, de propósito — é o servidor quem documenta isso: um fluxo
 * pela metade tem de poder ser salvo. `version` é o carimbo do rascunho que a
 * tela leu; sem ele, duas pessoas editando o mesmo rascunho fariam a última
 * gravação vencer em silêncio.
 */
export const salvarRascunho = (
  definicaoId: string,
  graph: Record<string, unknown>,
  version?: number,
) =>
  post<VersaoDeFluxo>(`${ADMIN}/${definicaoId}/draft`, {
    graph,
    ...(version === undefined ? {} : { version }),
  });

/** A lista **é** a resposta: zero problemas significa que publica. */
export const obterDiagnosticos = (versaoId: string) =>
  get<{ diagnostics: Diagnostico[] }>(`${ADMIN}/versions/${versaoId}/diagnostics`).then(
    (resposta) => resposta.diagnostics,
  );

export const publicarVersao = (versaoId: string) =>
  post<VersaoDeFluxo>(`${ADMIN}/versions/${versaoId}/publish`);

// ── chamado ────────────────────────────────────────────────────────────────

/** Vincula o chamado a uma versão publicada, no estado inicial dela. */
export const vincularFluxo = (ticketId: string, versionId: string) =>
  post<Chamado>(`${CHAMADOS}/${ticketId}/workflow`, { versionId });

/** O que dá para fazer a partir daqui, e por que o resto está apagado. */
export const obterTransicoes = (ticketId: string) =>
  get<TransicoesDoChamado>(`${CHAMADOS}/${ticketId}/transitions`);

export const executarTransicao = (ticketId: string, pedido: PedidoDeTransicao) =>
  post<{ ticket: Chamado; applied: PassoAplicado[] }>(
    `${CHAMADOS}/${ticketId}/transitions`,
    pedido,
  );

export const obterHistorico = (ticketId: string) =>
  get<RegistroDeTransicao[]>(`${CHAMADOS}/${ticketId}/transitions/history`);

// ── chaves do TanStack Query ───────────────────────────────────────────────

/**
 * Duas raízes, e não uma.
 *
 * `fluxos` é catálogo — muda em ritmo de administração. `transicoes` e
 * `historico` são do chamado e mudam a cada clique. Invalidar a raiz de
 * chamados depois de uma transição não deve arrastar a lista de definições
 * junto, nem o contrário.
 */
export const chavesDeFluxo = {
  raiz: ['fluxos'] as const,
  definicoes: ['fluxos', 'definicoes'] as const,
  diagnosticos: (versaoId: string) => ['fluxos', 'diagnosticos', versaoId] as const,
  /** O grafo que esta sessão viu, guardado no cache — ver a nota no topo. */
  grafo: (versaoId: string) => ['fluxos', 'grafo', versaoId] as const,
  transicoes: (ticketId: string) => ['chamados', 'item', ticketId, 'transicoes'] as const,
  historico: (ticketId: string) => ['chamados', 'item', ticketId, 'historico'] as const,
};
